import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, signal, computed } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MarketplaceService } from "../marketplace.service";
import { NotificationsService } from "../../../../../shared/_services/notifications.service";
import { StrategyTypes } from "../../../../../shared/_enums/strategy-types.enum";
import { RiskLevelTypes } from "../../../../../shared/_enums/risk-level-types.enum";
import { ScheduleTypes } from "../../../../../shared/_enums/schedule-types.enum";
import { TradingSymbolsListsService, TradingSymbolsListView } from "../../_services/trading-symbols-lists.service";
import { BotRunningSchedule } from "../../../../../shared/_types/bot-info.type";
import { FileStorageService } from "../../_services/file-storage.service";
import { formatDate } from "@angular/common";
import {
    MartingaleStrategyOptions,
    CustomStrategyOptions,
    WizardStrategyOptions,
    TreasuresStrategyOptions,
} from "../../../../../shared/_types/bot.type";
import {
    isMartingaleStrategyOptions,
    isCustomStrategyOptions,
} from "../../../../../shared/_helpers/strategy-options-type-check.helper";
import { StrategyGroupTypes } from "../../../../../shared/_enums/strategy-group-types.enum";

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: "app-edit-marketplace",
    templateUrl: "./edit-marketplace.component.html",
    styleUrl: "./edit-marketplace.component.scss",
    standalone: false,
})
export class EditMarketplaceComponent implements OnInit, OnDestroy {
    name = signal<string>("");
    strategyType = signal<StrategyTypes>(StrategyTypes.Unknown);
    strategyGroup = signal<StrategyGroupTypes>(StrategyGroupTypes.SingleSymbol);
    shortDescription = signal<string>("");
    description = signal<string>("");
    imageUrl = signal<string>("");
    file = signal<string>("");
    price = signal<number>(0);
    tradingSymbolsListId = signal<string>("");
    tradingSymbolsListName = signal<string>("");
    whatToExpectDescription = signal<string>("");
    riskLevel = signal<RiskLevelTypes>(RiskLevelTypes.Low);
    riskLevelDescription = signal<string>("");
    performanceDescription = signal<string>("");
    entryDescription = signal<string>("");
    exitDescription = signal<string>("");
    recoveryDescription = signal<string>("");
    recomendedParametersDescription = signal<string>("");
    scheduleDescription = signal<string>("");
    publisher = signal<string>("");
    runningSchedules = signal<BotRunningSchedule[]>([]);
    annualizedReturn = signal<number>(0);
    sharpeRation = signal<number>(0);
    maxDropdown = signal<number>(0);
    initialInvestment = signal<number>(0);
    finalValue = signal<number>(0);
    testedRangeStart = signal<string>(formatDate(new Date(), "yyyy-MM-ddTHH:mm", "en"));
    testedRangeEnd = signal<string>(formatDate(new Date(), "yyyy-MM-ddTHH:mm", "en"));
    winRate = signal<number>(0);

    touched = signal<{
        name: boolean;
        strategyType: boolean;
        strategyGroup: boolean;
        shortDescription: boolean;
        description: boolean;
        tradingSymbolsListId: boolean;
        riskLevel: boolean;
        testedRangeStart: boolean;
        testedRangeEnd: boolean;
    }>({
        name: false,
        strategyType: false,
        strategyGroup: false,
        shortDescription: false,
        description: false,
        tradingSymbolsListId: false,
        riskLevel: false,
        testedRangeStart: false,
        testedRangeEnd: false,
    });

    loading = signal<boolean>(false);
    isNew = signal<boolean>(true);
    isEditMode = signal<boolean>(false);
    botInfoId = signal<string | null>(null);
    strategyTypes: string[] = Object.keys(StrategyTypes).filter((key) => isNaN(Number(key)));
    strategyGroups: string[] = Object.keys(StrategyGroupTypes).filter((key) => isNaN(Number(key)));
    riskLevelTypes: string[] = Object.keys(RiskLevelTypes).filter((key) => isNaN(Number(key)));
    scheduleTypes: string[] = Object.keys(ScheduleTypes).filter((key) => isNaN(Number(key)));
    tradingSymbolsLists = signal<TradingSymbolsListView[]>([]);
    uploading = signal<boolean>(false);
    selectedFile = signal<File | null>(null);
    generatedFileName = signal<string>("");
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    strategyOptions = signal<
        | MartingaleStrategyOptions
        | CustomStrategyOptions
        | WizardStrategyOptions
        | TreasuresStrategyOptions
        | null
        | undefined
    >(null);
    isStrategyOptionsValid = signal<boolean>(true);

    public isMartingaleStrategyOptions: typeof isMartingaleStrategyOptions = isMartingaleStrategyOptions;
    public isCustomStrategyOptions: typeof isCustomStrategyOptions = isCustomStrategyOptions;
    public StrategyTypesEnum: typeof StrategyTypes = StrategyTypes;

    martingaleStrategyOptions = computed<MartingaleStrategyOptions | null | undefined>(() => {
        const options = this.strategyOptions();
        if (isMartingaleStrategyOptions(options)) {
            return options;
        }
        return null;
    });

    customStrategyOptions = computed<CustomStrategyOptions | null | undefined>(() => {
        const options = this.strategyOptions();
        if (isCustomStrategyOptions(options)) {
            return options;
        }
        return null;
    });

    isFormValid = computed(() => {
        return (
            this.name().trim().length > 0 &&
            this.name().length <= 100 &&
            this.strategyType() !== StrategyTypes.Unknown &&
            this.shortDescription().trim().length > 0 &&
            this.shortDescription().length <= 200 &&
            this.description().trim().length > 0 &&
            this.description().length <= 2000 &&
            this.price() >= 0 &&
            this.tradingSymbolsListId().trim().length > 0 &&
            this.testedRangeStart().trim().length > 0 &&
            this.testedRangeEnd().trim().length > 0 &&
            this.isStrategyOptionsValid()
        );
    });

    hasAtLeastOneBuySchedule = computed(() => {
        return this.runningSchedules().some((schedule) => schedule.buy === true);
    });

    hasAtLeastOneSellSchedule = computed(() => {
        return this.runningSchedules().some((schedule) => schedule.sell === true);
    });

    isRunningSchedulesValid = computed(() => {
        return (
            this.runningSchedules().length > 0 && this.hasAtLeastOneBuySchedule() && this.hasAtLeastOneSellSchedule()
        );
    });

    isSaveButtonDisabled = computed(() => {
        return !this.isFormValid() || !this.isRunningSchedulesValid() || this.loading();
    });

    isUploadButtonDisabled = computed(() => {
        const fileName = this.generatedFileName();
        return (
            !this.selectedFile() ||
            !fileName ||
            fileName.indexOf(".") === -1 ||
            fileName.indexOf(".") === 0 ||
            fileName.indexOf(".") === fileName.length - 1 ||
            fileName.indexOf(".") !== fileName.lastIndexOf(".") ||
            this.uploading()
        );
    });

    constructor(
        private marketplaceService: MarketplaceService,
        private tradingSymbolsListsService: TradingSymbolsListsService,
        private fileStorageService: FileStorageService,
        private router: Router,
        private route: ActivatedRoute,
        private notificationsService: NotificationsService,
    ) {}

    ngOnInit(): void {
        this.loadTradingSymbolsLists();

        this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params) => {
            this.botInfoId.set(params["id"]);
            this.isEditMode.set(!!(this.botInfoId() && this.botInfoId() !== "new"));

            if (this.isEditMode()) {
                this.loadBotInfo();
            } else {
                this.addDefaultRunningSchedules();
                this.initializeDefaultStrategyOptions();
            }
        });
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    getStrategyTypeValue(name: string): StrategyTypes {
        return StrategyTypes[name as keyof typeof StrategyTypes];
    }

    getStrategyGroupValue(name: string): StrategyGroupTypes {
        return StrategyGroupTypes[name as keyof typeof StrategyGroupTypes];
    }

    getRiskLevelValue(name: string): RiskLevelTypes {
        return RiskLevelTypes[name as keyof typeof RiskLevelTypes];
    }

    getStrategyTypeName(value: StrategyTypes): string {
        return StrategyTypes[value] as string;
    }

    getStrategyGroupName(value: StrategyGroupTypes): string {
        return StrategyGroupTypes[value] as string;
    }

    getRiskLevelName(value: RiskLevelTypes): string {
        return RiskLevelTypes[value] as string;
    }

    getScheduleTypeValue(name: string): ScheduleTypes {
        return ScheduleTypes[name as keyof typeof ScheduleTypes];
    }

    getScheduleTypeName(value: ScheduleTypes): string {
        return ScheduleTypes[value] as string;
    }

    addRunningSchedule(): void {
        const newSchedule: BotRunningSchedule = {
            id: undefined,
            botInfoId: undefined,
            runningSchedule: ScheduleTypes.None,
            sell: false,
            buy: false,
        };
        this.runningSchedules.update((schedules) => [...schedules, newSchedule]);
    }

    addDefaultRunningSchedules(): void {
        const schedule: BotRunningSchedule = {
            id: undefined,
            botInfoId: undefined,
            runningSchedule: ScheduleTypes.FifteenMinutesBeforeClose,
            sell: true,
            buy: true,
        };
        this.runningSchedules.set([schedule]);
    }

    initializeDefaultStrategyOptions(): void {
        const strategyType = this.strategyType();

        if (strategyType === StrategyTypes.Martingale) {
            this.strategyOptions.set({
                isDefault: false,
                type: StrategyTypes.Martingale,
                rsiLowerLimit: 0,
                rsiUpperLimit: 100,
                emA200Limit: null,
                minProfitLimitForSell: 2,
                buyBelowMA200: false,
                bids: [],
            });
        } else if (strategyType === StrategyTypes.Custom) {
            this.strategyOptions.set({
                isDefault: false,
                type: StrategyTypes.Custom,
                fields: [],
            });
        }
    }

    removeRunningSchedule(index: number): void {
        this.runningSchedules.update((schedules) => schedules.filter((_, i) => i !== index));
    }

    updateRunningSchedule(index: number, field: keyof BotRunningSchedule, value: any): void {
        this.runningSchedules.update((schedules) => {
            const updated = [...schedules];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }

    populateRunningSchedules(schedules: BotRunningSchedule[]): void {
        this.runningSchedules.set([...schedules]);
    }

    onStrategyOptionsChange(options: MartingaleStrategyOptions | CustomStrategyOptions): void {
        this.strategyOptions.set(options);
    }

    onStrategyOptionsValidityChange(isValid: boolean): void {
        this.isStrategyOptionsValid.set(isValid);
    }

    onStrategyTypeChange(): void {
        this.initializeDefaultStrategyOptions();
    }

    markFieldTouched(field: string): void {
        const current = this.touched();
        this.touched.set({ ...current, [field]: true });
    }

    getRunningSchedulesError(): string {
        if (this.runningSchedules().length === 0) {
            return "At least one running schedule is required";
        }
        if (!this.hasAtLeastOneBuySchedule()) {
            return "At least one schedule must allow buying";
        }
        if (!this.hasAtLeastOneSellSchedule()) {
            return "At least one schedule must allow selling";
        }
        return "";
    }

    loadTradingSymbolsLists(): void {
        this.tradingSymbolsListsService
            .list()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (lists) => {
                    this.tradingSymbolsLists.set(lists);
                },
                error: () => {
                    this.notificationsService.showError("Error", "Failed to load trading symbols lists");
                },
            });
    }

    loadBotInfo(): void {
        const id = this.botInfoId();
        if (!id) {
            return;
        }

        this.isNew.set(false);
        this.loading.set(true);
        this.marketplaceService
            .get(id)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (botInfo) => {
                    this.name.set(botInfo.name || "");
                    this.strategyType.set(botInfo.strategyType || StrategyTypes.Unknown);
                    this.strategyGroup.set(botInfo.strategyGroup || StrategyGroupTypes.SingleSymbol);
                    this.shortDescription.set(botInfo.shortDescription || "");
                    this.description.set(botInfo.description || "");
                    this.imageUrl.set(botInfo.imageUrl || "");
                    this.file.set(botInfo.file || "");
                    this.price.set(botInfo.price || 0);
                    this.tradingSymbolsListId.set(botInfo.tradingSymbolsListId || "");
                    this.tradingSymbolsListName.set(botInfo.tradingSymbolsListName || "");
                    this.whatToExpectDescription.set(botInfo.whatToExpectDescription || "");
                    this.riskLevel.set(botInfo.riskLevel || RiskLevelTypes.Low);
                    this.riskLevelDescription.set(botInfo.riskLevelDescription || "");
                    this.performanceDescription.set(botInfo.performanceDescription || "");
                    this.entryDescription.set(botInfo.entryDescription || "");
                    this.exitDescription.set(botInfo.exitDescription || "");
                    this.recoveryDescription.set(botInfo.recoveryDescription || "");
                    this.recomendedParametersDescription.set(botInfo.recomendedParametersDescription || "");
                    this.scheduleDescription.set(botInfo.scheduleDescription || "");
                    this.publisher.set(botInfo.publisher || "");
                    this.annualizedReturn.set(botInfo.annualizedReturn || 0);
                    this.sharpeRation.set(botInfo.sharpeRation || 0);
                    this.maxDropdown.set(botInfo.maxDropdown || 0);
                    this.initialInvestment.set(botInfo.initialInvestment || 0);
                    this.finalValue.set(botInfo.finalValue || 0);
                    this.testedRangeStart.set(
                        botInfo.testedRangeStart || formatDate(new Date(), "yyyy-MM-ddTHH:mm", "en"),
                    );
                    this.testedRangeEnd.set(botInfo.testedRangeEnd || formatDate(new Date(), "yyyy-MM-ddTHH:mm", "en"));
                    this.winRate.set(botInfo.winRate || 0);

                    this.populateRunningSchedules(botInfo.runningSchedules || []);

                    const strategyType: StrategyTypes = botInfo.strategyType || StrategyTypes.Unknown;
                    if (strategyType === StrategyTypes.Martingale || strategyType === StrategyTypes.Custom) {
                        if (botInfo.strategyOptions) {
                            this.strategyOptions.set({ ...botInfo.strategyOptions });
                        } else {
                            this.initializeDefaultStrategyOptions();
                        }
                    }

                    this.loading.set(false);
                },
                error: () => {
                    this.notificationsService.showError("Error", "Failed to load bot info");
                    this.loading.set(false);
                    this.router.navigate(["/marketplace"]);
                },
            });
    }

    onFileSelected(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            this.selectedFile.set(target.files[0]);
            this.generateFileName();
        }
    }

    generateFileName(): void {
        const nameValue = this.name();
        const file = this.selectedFile();
        const fileExtension = file ? file.name.split(".").pop() : "";

        const sanitizedName = nameValue.toLowerCase().replace(/[^a-z0-9.]/g, "_");

        this.generatedFileName.set(sanitizedName + (fileExtension ? "." + fileExtension : ""));
    }

    onFileNameChange(newFileName: string): void {
        this.generatedFileName.set(newFileName);
    }

    uploadFile(): void {
        const file = this.selectedFile();
        const fileName = this.generatedFileName();

        if (!file || !this.name()) {
            return;
        }

        this.uploading.set(true);

        this.fileStorageService
            .uploadFile(file, fileName, "images/strategy")
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (response) => {
                    this.imageUrl.set(response.url);
                    this.notificationsService.showSuccess("Success", "File uploaded successfully");
                    this.selectedFile.set(null);
                    this.generatedFileName.set("");
                    this.uploading.set(false);
                    const fileInput = document.getElementById("fileInput") as HTMLInputElement;
                    if (fileInput) {
                        fileInput.value = "";
                    }
                },
                error: () => {
                    this.notificationsService.showError("Error", "Failed to upload file");
                    this.uploading.set(false);
                },
            });
    }

    save(): void {
        if (!this.isFormValid()) {
            this.markAllFieldsTouched();
            return;
        }

        if (!this.isRunningSchedulesValid()) {
            this.notificationsService.showError("Error", this.getRunningSchedulesError());
            return;
        }

        this.loading.set(true);

        const formValue: any = {
            name: this.name(),
            strategyType: this.strategyType(),
            strategyGroup: this.strategyGroup(),
            shortDescription: this.shortDescription(),
            description: this.description(),
            imageUrl: this.imageUrl(),
            file: this.file(),
            price: this.price(),
            tradingSymbolsListId: this.tradingSymbolsListId(),
            tradingSymbolsListName: this.tradingSymbolsListName(),
            whatToExpectDescription: this.whatToExpectDescription(),
            riskLevel: this.riskLevel(),
            riskLevelDescription: this.riskLevelDescription(),
            performanceDescription: this.performanceDescription(),
            entryDescription: this.entryDescription(),
            exitDescription: this.exitDescription(),
            recoveryDescription: this.recoveryDescription(),
            recomendedParametersDescription: this.recomendedParametersDescription(),
            scheduleDescription: this.scheduleDescription(),
            publisher: this.publisher(),
            runningSchedules: this.runningSchedules(),
            annualizedReturn: this.annualizedReturn(),
            sharpeRation: this.sharpeRation(),
            maxDropdown: this.maxDropdown(),
            initialInvestment: this.initialInvestment(),
            finalValue: this.finalValue(),
            testedRangeStart: this.testedRangeStart(),
            testedRangeEnd: this.testedRangeEnd(),
            winRate: this.winRate(),
            strategyOptions: this.strategyOptions(),
        };

        if (this.isEditMode()) {
            this.marketplaceService
                .update(this.botInfoId()!, formValue)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: () => {
                        this.notificationsService.showSuccess("Success", "Bot info updated successfully");
                        this.router.navigate(["/marketplace"]);
                    },
                    error: () => {
                        this.notificationsService.showError("Error", "Failed to update bot info");
                        this.loading.set(false);
                    },
                });
        } else {
            this.marketplaceService
                .create(formValue)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: () => {
                        this.notificationsService.showSuccess("Success", "Bot info created successfully");
                        this.router.navigate(["/marketplace"]);
                    },
                    error: () => {
                        this.notificationsService.showError("Error", "Failed to create bot info");
                        this.loading.set(false);
                    },
                });
        }
    }

    cancel(): void {
        this.router.navigate(["/marketplace"]);
    }

    private markAllFieldsTouched(): void {
        this.touched.update(() => ({
            name: true,
            strategyType: true,
            strategyGroup: true,
            shortDescription: true,
            description: true,
            tradingSymbolsListId: true,
            riskLevel: true,
            testedRangeStart: true,
            testedRangeEnd: true,
        }));
    }

    getFieldError(fieldName: string): string {
        const touchedFields = this.touched();

        if (!touchedFields[fieldName as keyof typeof touchedFields]) {
            return "";
        }

        switch (fieldName) {
            case "name":
                if (this.name().trim().length === 0) {
                    return "Name is required";
                }
                if (this.name().length > 100) {
                    return "Name is too long (max 100 characters)";
                }
                break;
            case "strategyType":
                if (this.strategyType() === StrategyTypes.Unknown) {
                    return "Strategy Type is required";
                }
                break;
            case "shortDescription":
                if (this.shortDescription().trim().length === 0) {
                    return "Short Description is required";
                }
                if (this.shortDescription().length > 200) {
                    return "Short Description is too long (max 200 characters)";
                }
                break;
            case "description":
                if (this.description().trim().length === 0) {
                    return "Description is required";
                }
                if (this.description().length > 2000) {
                    return "Description is too long (max 2000 characters)";
                }
                break;
            case "price":
                if (this.price() < 0) {
                    return "Price must be at least 0";
                }
                break;
            case "tradingSymbolsListId":
                if (this.tradingSymbolsListId().trim().length === 0) {
                    return "Trading Symbols List is required";
                }
                break;
            case "testedRangeStart":
                if (this.testedRangeStart().trim().length === 0) {
                    return "Tested Range Start is required";
                }
                break;
            case "testedRangeEnd":
                if (this.testedRangeEnd().trim().length === 0) {
                    return "Tested Range End is required";
                }
                break;
        }

        return "";
    }
}
